import { CustomFormFlowEntitiesEnum, type ITrigger, type Maybe, type Nullable } from '@nova/core';

/**
 * Whether a trigger's flow is switched on. The check-in payload deliberately carries deactivated
 * triggers so callers can tell a form that is turned off apart from one that was never configured,
 * which means every caller that renders or reads a trigger has to exclude the disabled ones.
 */
export function isTriggerEnabled(trigger: Maybe<ITrigger>): boolean {
  return Boolean(trigger) && trigger!.isActive !== false;
}

/**
 * Finds the triggers scoped to a specific load type (as opposed to the warehouse-wide triggers of
 * the same kind), whether or not they are enabled. Callers decide what the result means: an empty
 * list is a load type with no form of its own, while a list holding only disabled triggers is a
 * form that exists but is switched off.
 *
 * One load-type form is split across one trigger per audience its fields were configured for, so
 * fields marked "visible to all" and fields marked "driver only" arrive as two triggers that
 * together make up a single form. The payload is already narrowed to the driver-visible
 * audiences server-side, but it carries every load type's triggers, hence the objectId match.
 */
export function findLoadTypeTriggers(
  triggers: ITrigger[] | undefined,
  loadTypeId: Nullable<string>
): ITrigger[] {
  if (!loadTypeId) {
    return [];
  }

  return (triggers ?? []).filter(
    trigger =>
      trigger.entityName === CustomFormFlowEntitiesEnum.loadtype && trigger.objectId === loadTypeId
  );
}
